import { useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { Button, Checkbox, MultiSelect, TextInput } from '@forwardflow/ui-v2'
import { ChevronRightIcon } from '@forwardflow/icons'
import { IFilterCriteria } from '@forwardflow/features/campaign-filter/interfaces/campaign-filter.types'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber.ts'
import { Card, Chip, LinkButton, StepHeader } from '../shell/Card'
import {
   compileDescription,
   IStarterCampaign,
   IVocabulary,
   nameFor,
   TCampaignKind,
   TLeadTypeId,
} from '../steps/first-campaign/starter-campaigns'
import { useLeadCount } from '../steps/first-campaign/useLeadCount'
import { useCyclingPlaceholder } from './useCyclingPlaceholder'

export interface ICampaignDraft {
   name: string
   criteria: IFilterCriteria[]
   campaignType: TCampaignKind[]
   leadTypes: TLeadTypeId[]
   understood: string[]
   bufferTime: number
   source?: string
   count?: number
}

const LEAD_TYPES: { id: TLeadTypeId; label: string }[] = [
   { id: 'buyer', label: 'Buyers' },
   { id: 'seller', label: 'Sellers' },
   { id: 'rental', label: 'Renters' },
]

const KINDS: { id: TCampaignKind; label: string; hint: string }[] = [
   { id: 'new-lead', label: 'New leads as they come in', hint: 'We call within minutes of a lead arriving' },
   { id: 'revive', label: 'Leads already in Follow Up Boss', hint: 'We work the existing list a few a day' },
]

const draftFromStarter = (s: IStarterCampaign): ICampaignDraft => ({
   name: s.campaignName,
   criteria: s.criteria,
   campaignType: s.campaignType,
   leadTypes: s.leadTypes,
   understood: [s.title],
   bufferTime: s.bufferTime,
   source: s.source,
   count: s.knownCount,
})

/**
 * "Who should we call?" in the customer's own words, turned into Follow Up
 * Boss filters they can see and correct. A starter campaign from the home
 * page arrives already compiled. Advanced filters expose the same fields
 * directly.
 */
export const WhoToCall = ({
   vocab,
   initialText,
   starter,
   initial,
   onDone,
}: {
   vocab: IVocabulary
   initialText?: string
   starter?: IStarterCampaign | null
   /** A draft being re-edited. */
   initial?: ICampaignDraft | null
   onDone: (draft: ICampaignDraft) => void
}) => {
   const [text, setText] = useState(initialText || '')
   const [advanced, setAdvanced] = useState(false)
   const [draft, setDraft] = useState<ICampaignDraft | null>(
      () => initial || (starter ? draftFromStarter(starter) : null)
   )
   const placeholder = useCyclingPlaceholder(!text)

   // Compile as the customer types (debounced). A starter is replaced the
   // moment they type something of their own.
   const typing = useRef<ReturnType<typeof setTimeout> | null>(null)
   const typedRef = useRef(false)
   useEffect(() => {
      if (typing.current) clearTimeout(typing.current)
      if (!text.trim()) {
         if (typedRef.current && !advanced) setDraft(null)
         return
      }
      if (text === initialText && draft && !typedRef.current) {
         // Arrived with text and a compiled draft already; nothing to redo.
         if (draft.understood.length) return
      }
      typedRef.current = true
      typing.current = setTimeout(() => {
         const compiled = compileDescription(text, vocab)
         // The customer's own words make the clearest campaign name.
         const typed = text.trim().replace(/\s+/g, ' ')
         const ownName = typed.length > 3 ? typed.charAt(0).toUpperCase() + typed.slice(1, 60) : ''
         setDraft({
            name: ownName || nameFor(compiled),
            criteria: compiled.criteria,
            campaignType: compiled.campaignType,
            leadTypes: compiled.leadTypes,
            understood: compiled.understood,
            bufferTime: compiled.campaignType.includes('new-lead') ? 2 : 0,
         })
      }, 350)
      return () => {
         if (typing.current) clearTimeout(typing.current)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [text, vocab])

   const nothingMatched = Boolean(draft) && draft!.criteria.length === 0 && text.trim().length > 0 && draft!.understood.length === 0

   const openAdvanced = () => {
      setAdvanced(true)
      if (!draft)
         setDraft({
            name: 'Custom campaign',
            criteria: [],
            campaignType: ['new-lead'],
            leadTypes: ['buyer', 'seller'],
            understood: [],
            bufferTime: 2,
         })
   }

   const valuesFor = (field: IFilterCriteria['field'], operator: IFilterCriteria['operator']) =>
      (draft?.criteria.find((c) => c.field === field && c.operator === operator)?.value || []).map(String)
   const setValues = (
      field: IFilterCriteria['field'],
      operator: IFilterCriteria['operator'],
      values: string[]
   ) => {
      if (!draft) return
      const rest = draft.criteria.filter((c) => !(c.field === field && c.operator === operator))
      const typed: Array<string | number> = field === 'assigned_pond_id' ? values.map(Number) : values
      setDraft({
         ...draft,
         criteria: values.length ? [...rest, { field, logic: 'AND', value: typed, operator }] : rest,
      })
   }
   const toggleLeadType = (id: TLeadTypeId) => {
      if (!draft) return
      const has = draft.leadTypes.includes(id)
      if (has && draft.leadTypes.length === 1) return
      setDraft({ ...draft, leadTypes: has ? draft.leadTypes.filter((t) => t !== id) : [...draft.leadTypes, id] })
   }

   const { count, loading } = useLeadCount(draft ? draft.criteria : null, draft?.count)

   const stageOptions = vocab.stages.map((s) => ({ value: s.id, label: s.name }))
   const tagOptions = vocab.tags.map((t) => ({ value: t, label: t }))
   const sourceOptions = vocab.sources.map((s) => ({ value: s, label: s }))
   const pondOptions = vocab.ponds.map((p) => ({ value: String(p.pondId), label: p.pondName }))

   const labelFor = (c: IFilterCriteria) => {
      const names = c.value.map((v) =>
         c.field === 'stage'
            ? vocab.stages.find((s) => s.id === String(v))?.name || String(v)
            : c.field === 'assigned_pond_id'
              ? vocab.ponds.find((p) => String(p.pondId) === String(v))?.pondName || String(v)
              : String(v)
      )
      const not = c.operator === 'NOT_CONTAINS_ANY' ? 'not ' : ''
      const what =
         c.field === 'source' ? 'from' : c.field === 'stage' ? 'in' : c.field === 'tags' ? 'tagged' : c.field === 'assigned_pond_id' ? 'in pond' : 'about'
      return `${not}${what} ${names.join(', ')}`
   }

   const summaryChips = useMemo(() => {
      if (!draft) return []
      const chips = draft.criteria.map(labelFor)
      const kind = draft.campaignType.includes('revive') ? 'older leads' : 'new leads as they arrive'
      const types =
         draft.leadTypes.length === 3
            ? 'buyers, sellers and renters'
            : draft.leadTypes.map((t) => (t === 'rental' ? 'renters' : `${t}s`)).join(' and ')
      return [...chips, kind, types]
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [draft, vocab])

   const ready = Boolean(draft) && !nothingMatched && count !== 0
   const buttonLabel = !draft
      ? 'Describe who to call, or pick a campaign'
      : count === 0
        ? 'No leads match, loosen a filter'
        : nothingMatched && !advanced
          ? 'Use names from Follow Up Boss, or open the filters'
          : count !== undefined
            ? `Call these ${formatNumber(count)} leads`
            : 'Call these leads'

   return (
      <div className="space-y-4">
         <Card>
            <StepHeader
               title="Who should we call?"
               lede="Say it the way you would to an assistant. We turn it into Follow Up Boss filters you can check below, and count the leads that match."
            />
            <TextInput
               value={text}
               placeholder={placeholder}
               onChange={(e) => setText(e.currentTarget.value)}
               onKeyDown={(e) => {
                  if (e.key === 'Enter' && ready && draft) onDone({ ...draft, count })
               }}
               autoFocus={!starter}
               data-testid="describe-leads"
            />

            {draft && (
               <div className="space-y-2" data-testid="draft-summary">
                  <p className="text-white/45 text-xs">We understood</p>
                  {nothingMatched && !advanced ? (
                     <p className="text-white/70 text-sm">
                        We couldn't match that to a source, stage, tag or pond in your Follow Up
                        Boss. Use the names you use there, or open the filters below.
                     </p>
                  ) : (
                     <div className="flex flex-wrap items-center gap-1.5">
                        {summaryChips.map((c) => (
                           <Chip key={c}>{c}</Chip>
                        ))}
                        <span className="text-white/70 text-sm ml-auto" data-testid="lead-count">
                           {loading && count === undefined ? (
                              'Counting'
                           ) : count !== undefined ? (
                              <>
                                 <b className="text-white">{formatNumber(count)}</b>{' '}
                                 {count === 1 ? 'lead matches' : 'leads match'}
                              </>
                           ) : null}
                        </span>
                     </div>
                  )}
               </div>
            )}

            {!advanced ? (
               <LinkButton onClick={openAdvanced} testId="open-advanced">
                  Advanced filters
               </LinkButton>
            ) : (
               draft && (
                  <div className="space-y-3 border-t border-white/10 pt-4" data-testid="advanced-filters">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        <MultiSelect size="sm" label="From these sources" data={sourceOptions} value={valuesFor('source', 'CONTAINS_ANY')} onChange={(v) => setValues('source', 'CONTAINS_ANY', v)} searchable comboboxProps={{ withinPortal: true }} />
                        <MultiSelect size="sm" label="In these ponds" data={pondOptions} value={valuesFor('assigned_pond_id', 'CONTAINS_ANY')} onChange={(v) => setValues('assigned_pond_id', 'CONTAINS_ANY', v)} searchable comboboxProps={{ withinPortal: true }} />
                        <MultiSelect size="sm" label="In these stages" data={stageOptions} value={valuesFor('stage', 'CONTAINS_ANY')} onChange={(v) => setValues('stage', 'CONTAINS_ANY', v)} searchable comboboxProps={{ withinPortal: true }} />
                        <MultiSelect size="sm" label="Not in these stages" data={stageOptions} value={valuesFor('stage', 'NOT_CONTAINS_ANY')} onChange={(v) => setValues('stage', 'NOT_CONTAINS_ANY', v)} searchable comboboxProps={{ withinPortal: true }} />
                        <MultiSelect size="sm" label="With any of these tags" data={tagOptions} value={valuesFor('tags', 'CONTAINS_ANY')} onChange={(v) => setValues('tags', 'CONTAINS_ANY', v)} searchable comboboxProps={{ withinPortal: true }} />
                        <MultiSelect size="sm" label="Without these tags" data={tagOptions} value={valuesFor('tags', 'NOT_CONTAINS_ANY')} onChange={(v) => setValues('tags', 'NOT_CONTAINS_ANY', v)} searchable comboboxProps={{ withinPortal: true }} />
                     </div>
                     <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                        <div className="flex items-center gap-3">
                           <span className="text-white/60 text-sm">Lead types</span>
                           {LEAD_TYPES.map((t) => (
                              <label key={t.id} className="flex items-center gap-1.5 text-sm text-white cursor-pointer">
                                 <Checkbox size="xs" checked={draft.leadTypes.includes(t.id)} onChange={() => toggleLeadType(t.id)} />
                                 {t.label}
                              </label>
                           ))}
                        </div>
                     </div>
                     <div className="flex flex-wrap gap-2">
                        {KINDS.map((k) => {
                           const on = draft.campaignType[0] === k.id
                           return (
                              <button
                                 key={k.id}
                                 type="button"
                                 title={k.hint}
                                 onClick={() => setDraft({ ...draft, campaignType: [k.id], bufferTime: k.id === 'new-lead' ? 2 : 0 })}
                                 className={cn(
                                    'rounded-lg border px-3 py-1.5 text-sm transition-all',
                                    on
                                       ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.30)] text-white'
                                       : 'border-white/20 bg-white/5 text-white/70 hover:border-[#9F7BFF]/50'
                                 )}
                              >
                                 {k.label}
                              </button>
                           )
                        })}
                     </div>
                  </div>
               )
            )}

            <Button
               size="middle"
               className="!h-11 w-full"
               onClick={() => draft && onDone({ ...draft, count })}
               disabled={!ready}
               data-testid="call-these-leads"
            >
               {buttonLabel} <ChevronRightIcon width={16} />
            </Button>
            <p className="text-white/40 text-xs">
               Your stop rules apply on top of this: leads you are already working, finished with,
               or on your do-not-call list are never called.
            </p>
         </Card>
      </div>
   )
}
